'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, TrendingUp, Users } from 'lucide-react';

import { MetricCard } from './MetricCard';
import { EmptyState } from './EmptyState';

export type TimingMetricData = {
    metric: string;
    avgMinutes: number;
    medianMinutes: number;
    p95Minutes: number;
    totalConversions: number;
};

interface ConversionTimingMetricsProps {
    title: string;
    description: string;
    data: TimingMetricData | undefined;
    isLoading: boolean;
    error?: unknown;
}

function formatTime(minutes: number): string {
    if (minutes < 1) {
        return `${Math.round(minutes * 60)}s`;
    }
    if (minutes < 60) {
        return `${minutes.toFixed(1)}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
}

export function ConversionTimingMetrics({ title, description, data, isLoading, error }: ConversionTimingMetricsProps) {
    const { t } = useTranslation('analytics');

    if (error) {
        return (
            <MetricCard title={title} description={description}>
                <div className="flex items-center justify-center h-full">
                    <p className="text-destructive">{t('conversion.timing.errorMessage')}</p>
                </div>
            </MetricCard>
        );
    }

    return (
        <MetricCard title={title} description={description} isLoading={isLoading} dynamicHeight>
            {!data || data.totalConversions === 0 ? (
                <EmptyState message={t('conversion.timing.emptyState')} />
            ) : (
                <div className="space-y-4">
                    {/* Total Conversions */}
                    <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg border border-primary/10">
                        <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-primary" />
                            <span className="text-sm font-medium text-foreground">{t('conversion.timing.totalConversions')}</span>
                        </div>
                        <span className="text-lg font-semibold text-foreground">{data.totalConversions.toLocaleString()}</span>
                    </div>

                    {/* Timing Metrics */}
                    <div className="grid grid-cols-3 gap-3">
                        {/* Average */}
                        <div className="p-3 bg-muted/50 rounded-lg text-center">
                            <div className="flex items-center justify-center gap-1 mb-1">
                                <Clock className="h-3 w-3 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground">{t('conversion.timing.average')}</span>
                            </div>
                            <span className="text-lg font-semibold text-foreground">{formatTime(data.avgMinutes)}</span>
                        </div>

                        {/* Median */}
                        <div className="p-3 bg-muted/50 rounded-lg text-center">
                            <div className="flex items-center justify-center gap-1 mb-1">
                                <TrendingUp className="h-3 w-3 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground">{t('conversion.timing.median')}</span>
                            </div>
                            <span className="text-lg font-semibold text-foreground">{formatTime(data.medianMinutes)}</span>
                        </div>

                        {/* P95 */}
                        <div className="p-3 bg-muted/50 rounded-lg text-center">
                            <div className="flex items-center justify-center gap-1 mb-1">
                                <Clock className="h-3 w-3 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground">{t('conversion.timing.p95')}</span>
                            </div>
                            <span className="text-lg font-semibold text-foreground">{formatTime(data.p95Minutes)}</span>
                        </div>
                    </div>

                    {/* Description */}
                    <p className="text-xs text-muted-foreground">
                        {t('conversion.timing.p95Description')}
                    </p>
                </div>
            )}
        </MetricCard>
    );
}
